import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import OptionBox from '@app/src/components/Ui/OptionBox'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { ProvidersModalPurpose, useProvidersModal } from '@app/src/context/ProvidersModalContext'
import { RequestAutomationType } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/RequestAutomationType'
import RequestAutomationWarningAlert from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Warning/RequestAutomationWarningAlert'
import ImportProvidersModal from '@app/src/pages/ResourceCollection/Collections/Provider/ConnectProviders/ImportProvidersModal'
import ConnectOnlyProvidersModal from '@app/src/pages/ResourceCollection/Collections/Providers/ConnectOnlyProvidersModal'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Provider, ProviderOrganizationResult } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import { AmplitudeTrackingEvents, HALF_MINUTE_MS } from '@app/src/wf-constants'
import { EmailOutlined, FileDownloadOutlined, LinkOutlined } from '@mui/icons-material'
import AddBoxOutlined from '@mui/icons-material/AddBoxOutlined'
import HelpOutline from '@mui/icons-material/HelpOutline'
import LanguageOutlined from '@mui/icons-material/LanguageOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { Box, Button, List, ListItem, ListSubheader, Stack, useTheme } from '@mui/material'
import { capitalize } from 'lodash'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'
import type { DrawerViewProps } from '../DrawerView'
import DrawerView from '../DrawerView'
import DrawerViewAddProviderPlaceholder from './DrawerViewAddProviderPlaceholder'
import DrawerViewAddProviderReferral from './DrawerViewAddProviderReferral'
import DrawerViewConnectionStatuses from './DrawerViewConnectionStatuses'
import DrawerViewFindProvider from './DrawerViewFindProvider'

type DrawerViewAddProviderProps = {
  providerType: string
} & Omit<DrawerViewProps, 'title'>

const DrawerViewAddProvider: React.FC<DrawerViewAddProviderProps> = ({ providerType, ...props }) => {
  const { formatMessage } = useIntl()
  const { openDrawer, closeDrawer } = useDrawer()
  const theme = useTheme()
  const { handleOpenConnectDialog } = useProvidersModal()
  const { trackEvent } = useAmplitude()
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [isInvitationLoading, setIsInvitationLoading] = useState(false)
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isPlaceholderLoading, setIsPlaceholderLoading] = useState(false)

  const iconBgColor = theme.palette.success.light

  const { items: unlinkedProviders = [], isLoading: isLoadingProviders } = useFetchCollectionWithPost<Provider>({
    key: [FetchKey.ProviderCollection],
    endpoint: endpoints.providersCollection,
    payload: {
      filter: [{ name: 'linked', filters: [{ operator: Operators.EqualTo, value: false }] }],
      include: ['referralContact', 'organization'],
    },
  })

  const { data: exactMatches = [], isLoading: isLoadingExactMatches } = useFetchResourceWithPost<
    Array<ProviderOrganizationResult>,
    number[]
  >({
    endpoint: endpoints.bulkFindExactMatches,
    key: FetchKey.ExactMatchExists,
    body: unlinkedProviders.map(p => p.id),
    shouldShowErrorNotification: false,
    options: {
      enabled: Boolean(unlinkedProviders.length),
      staleTime: HALF_MINUTE_MS,
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, HALF_MINUTE_MS),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  })

  const providersWithoutInvite = unlinkedProviders.filter(p => p.linkStatus !== ProviderLinkStatus.Invited)

  const uninvitedCount = providersWithoutInvite.length
  const existingCount = exactMatches.length
  const isLoading = isLoadingProviders || isLoadingExactMatches

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true)
  }

  const handleOpenFindProvider = async () => {
    setIsSearchLoading(true)
    try {
      openDrawer(<DrawerViewFindProvider providerType={providerType} />)
    } finally {
      setIsSearchLoading(false)
    }
  }

  const handleOpenReferral = async () => {
    setIsInvitationLoading(true)
    try {
      openDrawer(<DrawerViewAddProviderReferral providerType={providerType} />)
    } finally {
      setIsInvitationLoading(false)
    }
  }

  const handleOpenPlaceholder = async () => {
    setIsPlaceholderLoading(true)
    try {
      openDrawer(<DrawerViewAddProviderPlaceholder providerType={providerType} />)
    } finally {
      setIsPlaceholderLoading(false)
    }
  }

  const handleOpenExistingCompanies = () => {
    trackEvent({
      name: AmplitudeTrackingEvents.Provider.ConnectOnlyFlow.BannerClicked,
      eventProps: {
        exact_matches_count: existingCount,
        source: 'add_provider_drawer',
      },
    })
    handleOpenConnectDialog(unlinkedProviders, ProvidersModalPurpose.ConnectOnlyFlow, exactMatches)
  }

  const handleOpenConnectionStatuses = () => {
    openDrawer(<DrawerViewConnectionStatuses />)
  }

  return (
    <>
      <DrawerView
        title={formatMessage(
          { id: 'resourceCollections.create.addAProvider' },
          { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }).toLowerCase() },
        )}
        {...props}
      >
        <Box display="flex" flexDirection="column" height="100%">
          <List
            sx={{ flexGrow: 1 }}
            subheader={
              <ListSubheader>
                {formatMessage(
                  { id: 'resourceCollections.create.addProviderSubheader' },
                  {
                    provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 2 }).toLowerCase(),
                  },
                )}
              </ListSubheader>
            }
          >
            <ListItem sx={{ p: 0 }}>
              <OptionBox
                title={formatMessage({ id: 'resourceCollections.create.providerExcel' }, { provider: providerType })}
                description={formatMessage(
                  { id: 'resourceCollections.create.providerExcelDescription' },
                  { provider: providerType },
                )}
                onClick={handleOpenImportModal}
                iconComponent={FileDownloadOutlined}
                iconBackgroundColor={theme.palette.grey[200]}
                data-testid="import-providers"
              />
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <OptionBox
                title={formatMessage(
                  { id: 'resourceCollections.create.createProviderPlaceholder' },
                  {
                    provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 1 }).toLowerCase(),
                  },
                )}
                description={formatMessage({ id: 'resourceCollections.create.createProviderPlaceholderDescription' })}
                onClick={handleOpenPlaceholder}
                iconComponent={AddBoxOutlined}
                iconBackgroundColor={theme.palette.grey[200]}
                data-testid="create-supplier-link-later"
                loading={isPlaceholderLoading}
              />
            </ListItem>

            <ListSubheader>
              {formatMessage(
                { id: 'resourceCollections.create.connectWithProviderSubheader' },
                { provider: formatMessage({ id: `general.${capitalize(providerType)}` }, { count: 2 }).toLowerCase() },
              )}
            </ListSubheader>
            <ListItem sx={{ p: 0 }}>
              <OptionBox
                title={formatMessage({ id: 'resourceCollections.create.bulkInvite' })}
                description={formatMessage({ id: 'resourceCollections.create.bulkInviteDescription' })}
                onClick={() =>
                  !isLoadingProviders &&
                  handleOpenConnectDialog(providersWithoutInvite, ProvidersModalPurpose.InitialInvitation)
                }
                iconComponent={EmailOutlined}
                iconBackgroundColor={iconBgColor}
                data-testid="bulk-invite-companies"
                disabled={isLoadingProviders || uninvitedCount === 0}
                loading={isLoadingProviders}
              />
            </ListItem>
            <ListItem sx={{ p: 0 }}>
              <OptionBox
                title={formatMessage({ id: 'resourceCollections.create.invitation' })}
                description={formatMessage({ id: 'resourceCollections.create.invitationDescription' })}
                onClick={handleOpenReferral}
                iconComponent={LinkOutlined}
                iconBackgroundColor={iconBgColor}
                data-testid="send-invitation-link"
                loading={isInvitationLoading}
              />
            </ListItem>
            {exactMatches.length > 0 && !isLoading && (
              <ListItem sx={{ p: 0 }}>
                <OptionBox
                  title={formatMessage(
                    { id: 'resourceCollections.create.existingCompanies' },
                    { count: existingCount },
                  )}
                  description={formatMessage({ id: 'resourceCollections.create.existingCompaniesDescription' })}
                  onClick={handleOpenExistingCompanies}
                  iconComponent={LanguageOutlined}
                  iconBackgroundColor={iconBgColor}
                  data-testid="existing-companies"
                  loading={isLoading}
                />
              </ListItem>
            )}
            <ListItem sx={{ p: 0 }}>
              <OptionBox
                title={formatMessage({ id: 'resourceCollections.create.findProviderTitle' })}
                description={formatMessage({ id: 'resourceCollections.create.findProviderDescription' })}
                onClick={handleOpenFindProvider}
                iconComponent={SearchIcon}
                iconBackgroundColor={iconBgColor}
                data-testid="find-suppliers"
                loading={isSearchLoading}
              />
            </ListItem>
          </List>
          <Stack p={2}>
            <RequestAutomationWarningAlert
              automationType={RequestAutomationType.FOR_EVERY_NEW_PROVIDER}
              providerType={providerType}
              closeDrawer={closeDrawer}
            />
          </Stack>
          <Box p={2} pt={0} display="flex" justifyContent="center">
            <Button
              variant="text"
              startIcon={<HelpOutline />}
              onClick={handleOpenConnectionStatuses}
              sx={{ textTransform: 'none' }}
            >
              {formatMessage({ id: 'resourceCollections.providers.connectOnlyFlow.connectionStatuses.title' })}
            </Button>
          </Box>
        </Box>
      </DrawerView>
      <ImportProvidersModal open={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} />
      <CreationModalProgressContextProvider>
        <ConnectOnlyProvidersModal />
      </CreationModalProgressContextProvider>
    </>
  )
}

export default DrawerViewAddProvider
